<div class="users-page">
  <div class="users-header">
    <div>
      <h1>Platform users</h1>
      <p>Every account on the platform, including company staff.</p>
    </div>
    <button mat-flat-button class="btn-submit" (click)="openCreateDialog()">
      <mat-icon>add</mat-icon>
      New user
    </button>
  </div>

  <div class="table-toolbar">
    <div class="count-card count-card--blue">
      <div class="count-card__icon"><span class="material-icons">group</span></div>
      <div class="count-card__text">
        <span class="count-card__main"><strong>{{ totalCount() }}</strong> Users</span>
        <span class="count-card__status">{{ roleFilterLabel }}</span>
      </div>
    </div>

    <app-filter-select
      [(ngModel)]="roleFilter"
      name="roleFilter"
      (ngModelChange)="onFilterChange()"
      [options]="roleOptions"
      allLabel="All roles"
      class="role-filter"
    />
  </div>

  <app-error-banner [message]="errorMessage()" />

  @if (isLoading()) {
    <p class="hint">Loading...</p>
  } @else if (users().length === 0) {
    <app-empty-state message="No users match this filter." />
  } @else {
    <table>
      <thead>
        <tr>
          <th>Email</th>
          <th>Name</th>
          <th>Role</th>
          <th>Company</th>
          <th>Status</th>
          <th>Last login</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        @for (user of users(); track user.id) {
          <tr [class.row-locked]="isCompanyLocked(user)" [title]="toggleTooltip(user)">
            <td>{{ user.email }}{{ user.id === ownUserId ? ' (you)' : '' }}</td>
            <td>{{ user.fullName }}</td>
            <td>
              <span class="role-cell">
                {{ user.role }}
                @if (user.id !== ownUserId) {
                  <button class="edit-role-btn" (click)="openChangeRoleDialog(user)" aria-label="Change role" title="Change role">
                    <svg class="edit-pen-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path class="edit-pen-icon__pen" d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z" />
                      <line class="edit-pen-icon__underline" x1="4" y1="21.5" x2="9.5" y2="21.5" />
                    </svg>
                  </button>
                }
              </span>
            </td>
            <td>
              @if (user.companyStatus) {
                <app-status-badge [status]="user.companyStatus" />
              } @else {
                <span class="hint">—</span>
              }
            </td>
            <td>
              <span class="status-cell">
                <app-status-badge [status]="user.enabled ? 'ACTIVE' : 'SUSPENDED'" [label]="user.enabled ? 'Enabled' : 'Disabled'" />
                @if (!user.enabled && user.disabledReason) {
                  <span class="reason-note" [matTooltip]="user.disabledReason">{{ user.disabledReason }}</span>
                }
              </span>
            </td>
            <td>{{ user.lastLoginAt ? (user.lastLoginAt | date: 'medium') : 'Never' }}</td>
            <td>
              @if (user.id !== ownUserId) {
                <button
                  class="user-toggle-btn"
                  [class.user-toggle-btn--danger]="user.enabled"
                  (click)="toggleUser(user)"
                  [disabled]="togglingUserId() === user.id || isCompanyLocked(user)"
                  [attr.aria-label]="user.enabled ? 'Disable user' : 'Enable user'"
                  [matTooltip]="isCompanyLocked(user) ? toggleTooltip(user) : (user.enabled ? 'Disable user' : 'Enable user')"
                  matTooltipPosition="above"
                >
                  @if (user.enabled) {
                    <svg class="user-icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="9" cy="7" r="4" />
                      <path d="M3 21v-2a4 4 0 0 1 4-4h4" />
                      <g class="user-icon__mark">
                        <line x1="15" y1="15" x2="21" y2="21" />
                        <line x1="21" y1="15" x2="15" y2="21" />
                      </g>
                    </svg>
                  } @else {
                    <svg class="user-icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="9" cy="7" r="4" />
                      <path d="M3 21v-2a4 4 0 0 1 4-4h4" />
                      <path class="user-icon__mark" d="M14 18.5l2.5 2.5l4.5-5" />
                    </svg>
                  }
                </button>
              }
            </td>
          </tr>
        }
      </tbody>
    </table>

    <div class="pagination">
      <div class="page-size-control">
        <span>Results per page</span>
        <app-page-size-select [sizes]="pageSizeOptions" [ngModel]="pageSize()" (ngModelChange)="onPageSizeChange($event)" name="pageSize" />
      </div>

      <div class="pagination-pill">
        <button class="page-nav" (click)="goToPage(page() - 1)" [disabled]="page() === 0">
          <span class="material-icons">chevron_left</span>
          Previous
        </button>
        @for (p of pageWindow; track $index) {
          @if (p === -1) {
            <span class="page-ellipsis">…</span>
          } @else {
            <button class="page-dot" [class.page-dot--active]="p === page()" (click)="goToPage(p)">
              {{ p + 1 }}
            </button>
          }
        }
        <button class="page-nav" (click)="goToPage(page() + 1)" [disabled]="page() === totalPages() - 1">
          Next
          <span class="material-icons">chevron_right</span>
        </button>
        <span class="pagination-divider"></span>
        <span class="pagination-summary">Showing {{ rangeStart }}–{{ rangeEnd }} of {{ totalCount() }} users</span>
      </div>
    </div>
  }
</div>
